<template>
  <article
    class="document-preview"
    :class="{ 'document-preview--selected': selected }"
    tabindex="0"
    role="button"
    :aria-pressed="selected"
    :aria-label="`${displayName} file`"
    @click="$emit('toggle', file)"
    @keydown.enter.prevent="$emit('toggle', file)"
    @keydown.space.prevent="$emit('toggle', file)"
  >
    <FileThumbnail :file="file" />

    <div class="document-preview__body">
      <div class="document-preview__meta">
        <span class="document-preview__extension">{{ extension }}</span>
        <span v-if="uploadDate" class="document-preview__date">{{ uploadDate }}</span>
      </div>
      <h3 class="document-preview__title" :title="displayName">{{ displayName }}</h3>
      <div class="document-preview__actions" @click.stop>
        <slot name="actions"></slot>
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed, defineEmits, defineProps } from "vue";
import { getFileExtension } from "@/utils/getFileExtension";
import FileThumbnail from "./FileThumbnail.vue";

defineEmits(["toggle"]);

const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});

const displayName = computed(() => props.file?.name || props.file?.title || "Untitled file");

const extension = computed(() => {
  const rawMeta = String(props.file?.extension || "")
    .replace(".", "")
    .toLowerCase();
  const fromMeta = /^[a-z0-9]{1,5}$/.test(rawMeta) ? rawMeta : "";
  const fromPath = getFileExtension(props.file?.file, "");
  const fromName = getFileExtension(props.file?.name || props.file?.title, "");
  const value = fromMeta || fromPath || fromName || props.file?.type || "file";

  return String(value).replace(".", "").toUpperCase();
});

const uploadDate = computed(() => {
  const value = props.file?.created_at || props.file?.updated_at || props.file?.date;
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(parsed);
});
</script>

<style scoped lang="scss">
@import "@/styles/document-preview/document-preview";
</style>
